import { mockHotels } from './api/mockData';
import { cityCountryOf } from './location';

/**
 * Destination lookup for the search box autosuggest. Two kinds of result:
 * cities (a broad type-ahead list) and the actual hotels (so a hotel-name
 * search surfaces the property directly). This is a type-ahead aid, not the
 * inventory — searching still filters the live/mock hotels, so picking a
 * city with no matching property just yields an empty result set.
 */
export interface Destination {
  city: string;
  country: string;
}

export type PlaceKind = 'city' | 'hotel';

export interface Place {
  kind: PlaceKind;
  /** Primary text and the value written into the destination field. */
  label: string;
  /** Secondary text — the country for a city, "Hotel · City" for a hotel. */
  sublabel: string;
  id?: string;
  hid?: number;
}

export const DESTINATIONS: Destination[] = [
  // Asia-Pacific
  { city: 'Bangkok', country: 'Thailand' },
  { city: 'Phuket', country: 'Thailand' },
  { city: 'Krabi', country: 'Thailand' },
  { city: 'Chiang Mai', country: 'Thailand' },
  { city: 'Pattaya', country: 'Thailand' },
  { city: 'Hoi An', country: 'Vietnam' },
  { city: 'Hanoi', country: 'Vietnam' },
  { city: 'Ho Chi Minh City', country: 'Vietnam' },
  { city: 'Da Nang', country: 'Vietnam' },
  { city: 'Nha Trang', country: 'Vietnam' },
  { city: 'Bali', country: 'Indonesia' },
  { city: 'Jakarta', country: 'Indonesia' },
  { city: 'Yogyakarta', country: 'Indonesia' },
  { city: 'Singapore', country: 'Singapore' },
  { city: 'Kuala Lumpur', country: 'Malaysia' },
  { city: 'Penang', country: 'Malaysia' },
  { city: 'Manila', country: 'Philippines' },
  { city: 'Cebu', country: 'Philippines' },
  { city: 'Siem Reap', country: 'Cambodia' },
  { city: 'Phnom Penh', country: 'Cambodia' },
  { city: 'Maldives', country: 'Maldives' },
  { city: 'Malé', country: 'Maldives' },
  { city: 'Colombo', country: 'Sri Lanka' },
  { city: 'Kathmandu', country: 'Nepal' },
  { city: 'Tokyo', country: 'Japan' },
  { city: 'Kyoto', country: 'Japan' },
  { city: 'Osaka', country: 'Japan' },
  { city: 'Sapporo', country: 'Japan' },
  { city: 'Seoul', country: 'South Korea' },
  { city: 'Busan', country: 'South Korea' },
  { city: 'Hong Kong', country: 'Hong Kong' },
  { city: 'Macau', country: 'Macau' },
  { city: 'Taipei', country: 'Taiwan' },
  { city: 'Shanghai', country: 'China' },
  { city: 'Beijing', country: 'China' },
  // India
  { city: 'New Delhi', country: 'India' },
  { city: 'Mumbai', country: 'India' },
  { city: 'Bengaluru', country: 'India' },
  { city: 'Chennai', country: 'India' },
  { city: 'Kolkata', country: 'India' },
  { city: 'Hyderabad', country: 'India' },
  { city: 'Goa', country: 'India' },
  { city: 'Jaipur', country: 'India' },
  { city: 'Udaipur', country: 'India' },
  { city: 'Agra', country: 'India' },
  { city: 'Kochi', country: 'India' },
  // Middle East
  { city: 'Dubai', country: 'United Arab Emirates' },
  { city: 'Abu Dhabi', country: 'United Arab Emirates' },
  { city: 'Doha', country: 'Qatar' },
  { city: 'Riyadh', country: 'Saudi Arabia' },
  { city: 'Jeddah', country: 'Saudi Arabia' },
  { city: 'Muscat', country: 'Oman' },
  { city: 'Manama', country: 'Bahrain' },
  { city: 'Kuwait City', country: 'Kuwait' },
  { city: 'Amman', country: 'Jordan' },
  { city: 'Tel Aviv', country: 'Israel' },
  { city: 'Istanbul', country: 'Turkey' },
  // Europe
  { city: 'London', country: 'United Kingdom' },
  { city: 'Edinburgh', country: 'United Kingdom' },
  { city: 'Manchester', country: 'United Kingdom' },
  { city: 'Dublin', country: 'Ireland' },
  { city: 'Paris', country: 'France' },
  { city: 'Nice', country: 'France' },
  { city: 'Amsterdam', country: 'Netherlands' },
  { city: 'Brussels', country: 'Belgium' },
  { city: 'Berlin', country: 'Germany' },
  { city: 'Munich', country: 'Germany' },
  { city: 'Frankfurt', country: 'Germany' },
  { city: 'Zurich', country: 'Switzerland' },
  { city: 'Geneva', country: 'Switzerland' },
  { city: 'Vienna', country: 'Austria' },
  { city: 'Prague', country: 'Czechia' },
  { city: 'Budapest', country: 'Hungary' },
  { city: 'Warsaw', country: 'Poland' },
  { city: 'Rome', country: 'Italy' },
  { city: 'Milan', country: 'Italy' },
  { city: 'Venice', country: 'Italy' },
  { city: 'Florence', country: 'Italy' },
  { city: 'Madrid', country: 'Spain' },
  { city: 'Barcelona', country: 'Spain' },
  { city: 'Lisbon', country: 'Portugal' },
  { city: 'Porto', country: 'Portugal' },
  { city: 'Athens', country: 'Greece' },
  { city: 'Santorini', country: 'Greece' },
  { city: 'Copenhagen', country: 'Denmark' },
  { city: 'Stockholm', country: 'Sweden' },
  { city: 'Oslo', country: 'Norway' },
  { city: 'Helsinki', country: 'Finland' },
  { city: 'Reykjavik', country: 'Iceland' },
  // Americas
  { city: 'New York', country: 'United States' },
  { city: 'Los Angeles', country: 'United States' },
  { city: 'San Francisco', country: 'United States' },
  { city: 'Las Vegas', country: 'United States' },
  { city: 'Chicago', country: 'United States' },
  { city: 'Miami', country: 'United States' },
  { city: 'Boston', country: 'United States' },
  { city: 'Honolulu', country: 'United States' },
  { city: 'Toronto', country: 'Canada' },
  { city: 'Vancouver', country: 'Canada' },
  { city: 'Montreal', country: 'Canada' },
  { city: 'Mexico City', country: 'Mexico' },
  { city: 'Cancún', country: 'Mexico' },
  { city: 'Rio de Janeiro', country: 'Brazil' },
  { city: 'São Paulo', country: 'Brazil' },
  { city: 'Buenos Aires', country: 'Argentina' },
  { city: 'Lima', country: 'Peru' },
  { city: 'Santiago', country: 'Chile' },
  // Africa
  { city: 'Cairo', country: 'Egypt' },
  { city: 'Marrakech', country: 'Morocco' },
  { city: 'Cape Town', country: 'South Africa' },
  { city: 'Nairobi', country: 'Kenya' },
  { city: 'Zanzibar', country: 'Tanzania' },
  // Oceania
  { city: 'Sydney', country: 'Australia' },
  { city: 'Melbourne', country: 'Australia' },
  { city: 'Brisbane', country: 'Australia' },
  { city: 'Gold Coast', country: 'Australia' },
  { city: 'Auckland', country: 'New Zealand' },
  { city: 'Queenstown', country: 'New Zealand' },
  { city: 'Nadi', country: 'Fiji' },
];

const CITY_TO_COUNTRY: Record<string, string> = {};
for (const d of DESTINATIONS) {
  CITY_TO_COUNTRY[d.city.toLowerCase()] = d.country;
}

const COUNTRY_CODE_TO_NAME: Record<string, string> = {
  fr: 'France',
  us: 'United States',
  ae: 'United Arab Emirates',
  gb: 'United Kingdom',
  uk: 'United Kingdom',
  th: 'Thailand',
  vn: 'Vietnam',
  id: 'Indonesia',
  my: 'Malaysia',
  sg: 'Singapore',
  jp: 'Japan',
  kr: 'South Korea',
  it: 'Italy',
  es: 'Spain',
  de: 'Germany',
  mv: 'Maldives',
  cn: 'China',
  hk: 'Hong Kong',
  in: 'India',
  au: 'Australia',
  ca: 'Canada',
  mx: 'Mexico',
  br: 'Brazil',
  ch: 'Switzerland',
  at: 'Austria',
  nl: 'Netherlands',
  gr: 'Greece',
  pt: 'Portugal',
  tr: 'Turkey',
  eg: 'Egypt',
  za: 'South Africa',
  nz: 'New Zealand',
};

export function getCountryForCityOrCode(city?: string, code?: string): string | undefined {
  if (city && CITY_TO_COUNTRY[city.trim().toLowerCase()]) {
    return CITY_TO_COUNTRY[city.trim().toLowerCase()];
  }
  if (code && COUNTRY_CODE_TO_NAME[code.trim().toLowerCase()]) {
    return COUNTRY_CODE_TO_NAME[code.trim().toLowerCase()];
  }
  return undefined;
}

const CITY_PLACES: Place[] = DESTINATIONS.map((d) => ({
  kind: 'city',
  label: d.city,
  sublabel: d.country,
}));

const HOTEL_PLACES: Place[] = mockHotels.map((h) => ({
  kind: 'hotel',
  label: h.name,
  sublabel: `Hotel · ${cityCountryOf(h.address)}`,
  id: h.id,
  hid: h.hid,
}));

/**
 * Find a known hotel place by exact or case-insensitive label match.
 */
export function findHotelPlace(query: string): Place | undefined {
  const q = query.trim().toLowerCase();
  if (!q) return undefined;
  return HOTEL_PLACES.find((h) => h.label.toLowerCase() === q);
}

/**
 * The autocomplete hotel a typed name should open, if any. A destination with
 * the same name wins: "Barcelona" searches the city, not the hotel in Andorra
 * that is also called Barcelona.
 */
export function exactHotelMatch(
  data: Api.Search.Destinations.Response['data'] | undefined,
  query: string
): Api.Search.Destinations.Hotel | undefined {
  const q = query.trim().toLowerCase();
  const hotel = data?.hotels?.[0];
  if (!q || !hotel || hotel.name.toLowerCase() !== q) return undefined;
  return data?.regions?.some((region) => region.name.toLowerCase() === q) ? undefined : hotel;
}

export interface HotelHrefParams {
  id: string | number;
  checkin?: string | null;
  checkout?: string | null;
  occ?: string;
  residency?: string;
}

export function buildHotelHref(params: HotelHrefParams): string {
  const query = new URLSearchParams();
  if (params.checkin) query.set('checkin', params.checkin);
  if (params.checkout) query.set('checkout', params.checkout);
  if (params.occ) query.set('occ', params.occ);
  if (params.residency) query.set('residency', params.residency);
  const qStr = query.toString();
  return `/hotel/${encodeURIComponent(String(params.id))}${qStr ? `?${qStr}` : ''}`;
}

export interface SearchHrefParams {
  destination?: string | null;
  checkin?: string | null;
  checkout?: string | null;
  occ?: string;
  residency?: string;
}

export function buildSearchHref(params: SearchHrefParams): string {
  const query = new URLSearchParams();
  if (params.destination) query.set('destination', params.destination);
  if (params.checkin) query.set('checkin', params.checkin);
  if (params.checkout) query.set('checkout', params.checkout);
  if (params.occ) query.set('occ', params.occ);
  if (params.residency) query.set('residency', params.residency);
  const qStr = query.toString();
  return `/search${qStr ? `?${qStr}` : ''}`;
}

/**
 * Prefix matches rank above substring matches, so "ban" surfaces "Bangkok"
 * and "pen" surfaces "The Peninsula Bangkok". Hotels are considered before
 * cities so a clear hotel-name intent isn't crowded out. Case-insensitive;
 * capped at `limit` (default 5).
 */
export function searchPlaces(query: string, limit = 5): Place[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const starts: Place[] = [];
  const contains: Place[] = [];

  for (const place of [...CITY_PLACES, ...HOTEL_PLACES]) {
    const label = place.label.toLowerCase();
    if (place.kind === 'city') {
      const country = place.sublabel.toLowerCase();
      if (label.startsWith(q) || country.startsWith(q)) starts.push(place);
      else if (label.includes(q) || country.includes(q)) contains.push(place);
    } else {
      // Match hotels on their name only; the "Hotel · City" sublabel's city
      // is already represented by a city entry.
      if (label.startsWith(q)) starts.push(place);
      else if (label.includes(q)) contains.push(place);
    }
  }

  return [...starts, ...contains].slice(0, limit);
}
